import { LogLevel, Configuration as MsalConfig, RedirectRequest } from '@azure/msal-browser'
import { NavConfig } from '../components/global-nav/configs/base'
import { getConfiguration } from './get-configuration'
import {
	appClientId,
	authBrand,
	authLocale,
	channel,
	countryDataAttribute,
	environmentDataAttribute,
	loginHint as hint,
	languageDataAttribute,
	logoutUrl,
	requiredMIVal,
	requiredVal,
	sessionStorageKeyPrefixAttr,
	successTarget,
	useDataAttributes,
} from './script-attributes'
import { getWaypointTarget } from './waypoint-utils'

// Constants
const DATA_ATTRIBUTES_ENABLED = 'true'
const AUTH_REQUIRED_DISABLED = 'false'
const STATE_DELIMITER = '|'
const RCSIT_IDENTIFIER = 'rcsit'
const DEFAULT_STORAGE_LOCATION = 'localStorage'

/**
 * MSAL logger callback that filters out PII and routes messages to appropriate console methods
 */
const msalLoggerCallback = (level: LogLevel, message: string, containsPii: boolean): void => {
	if (containsPii) {
		return
	}
	switch (level) {
		case LogLevel.Error:
			console.error(message)
			return
		case LogLevel.Info:
			console.info(message)
			return
		case LogLevel.Verbose:
			console.debug(message)
			return
		case LogLevel.Warning:
			console.warn(message)
			return
	}
}

export interface FinalAuthConfig {
	authRequired: boolean
	locale: string
	loginHint: string
	msalConfig: MsalConfig
	redirectRequest: RedirectRequest
	region: string
	sessionStorageKeyPrefix: string
	signUpRequest: RedirectRequest
}

interface ConfigurationData {
	locale: string
	scopes: string[]
	region: string
	postLogoutURL: string
	applicationClientId: string
	authority: string
	approvedAuthorities: string[]
}

/**
 * Base request configuration for OAuth/MSAL redirects
 */
interface BaseAuthRequest {
	scopes: string[]
	redirectUri: string
	state: string
}

/**
 * Load configuration from either data attributes or NavConfig object
 *
 * Caches the result to avoid duplicate getConfiguration() calls.
 * When data attributes are enabled, reads configuration from HTML attributes,
 * otherwise falls back to the NavConfig object.
 *
 * @param config - Navigation configuration object
 * @returns Consolidated configuration data including locale, scopes, and auth settings
 */
const loadConfiguration = (config: NavConfig): ConfigurationData => {
	if (useDataAttributes === DATA_ATTRIBUTES_ENABLED) {
		const safeConfig = getConfiguration(environmentDataAttribute, countryDataAttribute, languageDataAttribute)
		const region = countryDataAttribute.toUpperCase()
		config._urls = safeConfig.urls

		return {
			locale: `${languageDataAttribute}-${region}`,
			scopes: safeConfig.mapAuthScopes,
			region,
			postLogoutURL: logoutUrl || safeConfig.mapPostLogoutRedirectURI,
			applicationClientId: safeConfig.mapClientId,
			authority: safeConfig.mapAuthority,
			approvedAuthorities: safeConfig.mapApprovedAuthorities,
		}
	} else {
		const region = config.region.toUpperCase()
		return {
			locale: authLocale || `${config.language}-${region}`,
			scopes: config.auth.authScopes,
			region,
			postLogoutURL: logoutUrl || config.urls.logoutURL,
			applicationClientId: appClientId || config.auth.clientID,
			authority: config.auth.authority,
			approvedAuthorities: config.auth.approvedAuthorities,
		}
	}
}

/**
 * Generate complete authentication configuration for MSAL and OAuth flows
 *
 * Note: Uses regular function declaration (not arrow function) for:
 * - Better stack traces with named function in error logs
 * - Clearer debugging experience in development tools
 * - Standard pattern for exported public API functions
 *
 * @param config - Navigation configuration containing auth settings
 * @param storageLocation - Optional cache storage location ('localStorage' or 'sessionStorage'). Defaults to 'localStorage'
 * @returns Complete authentication configuration including MSAL config and redirect requests
 */
export function getAuthConfig(config: NavConfig, storageLocation: string = DEFAULT_STORAGE_LOCATION): FinalAuthConfig {
	const loginHint = hint?.trim() ?? ''
	const sessionStorageKeyPrefix = sessionStorageKeyPrefixAttr
	const authRequired = Boolean(requiredVal && requiredVal !== AUTH_REQUIRED_DISABLED)

	// Load configuration once and reuse throughout the function
	const configData = loadConfiguration(config)
	const { locale, scopes, region } = configData

	const extraQueryParameters = getExtraQueryParams(config, locale)
	// Encode state with current URL and locale for OAuth redirect flow
	const regularState = `${document.URL}${STATE_DELIMITER}${locale}`

	const baseRequest = {
		scopes,
		redirectUri: getWaypointTarget(),
		// Custom state parameter to send any arbitrary information
		state: regularState,
	}

	// Look into custom state parameter: https://docs.microsoft.com/en-us/azure/active-directory/develop/msal-js-pass-custom-state-authentication-request
	const redirectRequest = createRedirectRequest(baseRequest, extraQueryParameters)

	const signUpRequest = createRedirectRequest(baseRequest, extraQueryParameters, () => false)

	return {
		authRequired,
		locale,
		loginHint,
		msalConfig: getMsalConfig(configData, storageLocation),
		redirectRequest,
		region,
		sessionStorageKeyPrefix,
		signUpRequest,
	}
}

/**
 * Create a redirect request object with common configuration
 *
 * @param baseRequest - Base request configuration containing scopes, redirectUri, and state
 * @param extraQueryParameters - Additional query parameters for Azure B2C
 * @param onRedirectNavigate - Optional callback to control redirect navigation behavior
 * @returns Configured RedirectRequest object
 */
const createRedirectRequest = (
	baseRequest: BaseAuthRequest,
	extraQueryParameters: Record<string, string>,
	onRedirectNavigate?: () => boolean
): RedirectRequest => {
	const request: RedirectRequest = {
		extraQueryParameters,
		...baseRequest,
	}

	if (onRedirectNavigate) {
		request.onRedirectNavigate = onRedirectNavigate
	}

	return request
}

/**
 * Build extra query parameters for Azure B2C authentication
 *
 * Determines the appropriate brand, channel (including rcsit suffix if needed),
 * and locale settings to pass to the Azure B2C policy page.
 *
 * @param config - Navigation configuration object
 * @param locale - Current locale string (e.g., 'en-US')
 * @returns Object containing query parameters for Azure B2C
 */
const getExtraQueryParams = (config: NavConfig, locale: string) => {
	const isRcsitURL = window.location.href.includes(RCSIT_IDENTIFIER)
	let channelVal = channel || config.channel
	if (isRcsitURL && !channel) {
		channelVal = `${config.channel}${RCSIT_IDENTIFIER}`
	}

	// Any query parameters that should be sent to the policy page on Azure B2C
	return {
		brand: authBrand || config.brand,
		channel: channelVal,
		requiredMissingInfo: requiredMIVal,
		ui_locales: locale,
	}
}

/**
 * Build MSAL (Microsoft Authentication Library) configuration object
 *
 * Creates the configuration for MSAL browser authentication including
 * auth settings, cache location, and logging configuration.
 *
 * @param configData - Configuration data containing auth settings
 * @param storageLocation - Cache storage location ('localStorage' or 'sessionStorage')
 * @returns Complete MSAL configuration object
 */
const getMsalConfig = (configData: ConfigurationData, storageLocation: string) => {
	const { postLogoutURL, applicationClientId, authority, approvedAuthorities } = configData

	const msalConfig: MsalConfig = {
		auth: {
			clientId: applicationClientId,
			authority,
			knownAuthorities: approvedAuthorities,
			redirectUri: successTarget,
			// Below Flag is used to enable/disable returning to origin page of LoginRequests
			navigateToLoginRequestUrl: false,
			// Below Property is used to determine where user ends after calling log out
			postLogoutRedirectUri: postLogoutURL,
		},
		cache: {
			cacheLocation: storageLocation,
			// Set this to 'true' to save cache in cookies to address trusted zones limitations in IE (see: https://github.com/AzureAD/microsoft-authentication-library-for-js/wiki/Known-issues-on-IE-and-Edge-Browser)
			storeAuthStateInCookie: false,
		},
		//Below is the configuration to set up interaction with loggers from MSAL
		//See https://docs.microsoft.com/en-us/azure/active-directory/develop/msal-logging-js for more information
		system: {
			loggerOptions: {
				loggerCallback: msalLoggerCallback,
				piiLoggingEnabled: false,
			},
		},
	}

	return msalConfig
}
